<template>

    <!-- loading -->
    <div v-if="state.sales_loading" class="my-2 text-left">
        <LoadingSection />
    </div>

    <Message
        :active="!can_add_to_sale.can_add_to_sale"
        type="error"
        :heading="'You can\'t add this vehicle to a sale because: '"
        :items="can_add_to_sale.reasons"
        class="mb-2"
    />

    <!-- ERROR -->
    <div v-if="state.error_message" class="my-2 text-center">
        <p>{{ state.error_message }}</p>
    </div>
        
    <!-- allow user to search for or select suggested lot nos -->
    <div v-if="!state.sales_loading" class="my-2 text-left">
        <EmptyList
            v-if="!visible_sales.length"
            heading="No eligible sales"
            message="No sales have been set up for the sale code you gave this vehicle"
        />
        <ul v-if="visible_sales.length" role="list" class="divide-y divide-gray-100">
            <AssetAddToSaleItem
                v-for="sale in visible_sales"
                :key="sale.id"
                :sale="sale"
                :asset_id="asset_id"
                :refresh="refresh"
                @added="selected_sale => saleSelected(selected_sale)"
            />
            <button
                class="rounded bg-blue-50 px-2 py-1 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-100 w-full mt-4"
                v-if="show_more_enabled"
                v-on:click="() => state.show_all_sales = !state.show_all_sales"
            >{{ state.show_all_sales ? 'Show less' : 'Show more' }}</button>
        </ul>  
    </div>

</template>

<script>
import {onMounted, watch, reactive, computed} from 'vue'
import * as SaleService from '@ser/sale'
import * as AssetService from '@ser/asset'
import { useSystemStore } from '@sto/system.store'
import AssetAddToSaleItem from '@par/asset/AssetAddToSaleItem.vue'
import EmptyList from "@glo/EmptyList.vue";
import Message from "@glo/Message.vue";
export default {
    props: ['asset_id', 'refresh'],
    components: {
        Message,
        EmptyList,
        AssetAddToSaleItem
    },
	setup(props, {emit}) {

        const state = reactive({
            sales: [],
            sales_loading: false,
            sales_loaded: false,
            asset_loading: false,
            show_all_sales: false,
            show_few_qty: 3,
            error_message: null,
            asset: null
        })

        const pagination = reactive({
            current_page: 1,
            per_page: 50,
            last_page: 1,
            total: 0,
        })

        const is_last_page = computed(() => {
            return pagination.total > 0 && pagination.current_page == pagination.last_page
        })

        const is_first_page = computed(() => {
            return pagination.current_page == 1
        })

        const visible_sales = computed(() => {
            return state.show_all_sales ? state.sales : state.sales.slice(0, state.show_few_qty);
        })

        const show_more_enabled = computed(() => {
            return pagination.total > state.show_few_qty
        })

        const can_add_to_sale = computed(() => {
            // let's not interfere with the user if they haven't selected a vehicle yet'
            if (!state.asset) return {
                can_add_to_sale: true,
                reasons: []
            }

            let outcome = {
                can_add_to_sale: true,
                reasons: []
            }

            if (!['ready'].includes(state.asset.status)) {
                outcome.can_add_to_sale = false
                outcome.reasons.push('Vehicle does not have the status "Ready"')
            }

            if (!state.asset.lot_number) {
                outcome.can_add_to_sale = false
                outcome.reasons.push('Vehicle does not have a lot number')
            }

            if (!state.asset.sale_code_id) {
                outcome.can_add_to_sale = false
                outcome.reasons.push('Vehicle does not have a sale code')
            }

            if (!state.asset.sale_section_id) {
                outcome.can_add_to_sale = false
                outcome.reasons.push('Vehicle does not have a sale section')
            }

            if (!state.asset.seller_account_id) {
                outcome.can_add_to_sale = false
                outcome.reasons.push('Vehicle does not have a seller account')
            }

            return outcome
        })

        const getPaginationPage = page => {
            if (page > pagination.last_page || page < 1) return
            pagination.current_page = page
            getEligibleSales()
            window.scrollTo({top: 0, behavior: 'smooth'})
        }

        const getPaginationNext = () => {
            if (!is_last_page.value) getPaginationPage(pagination.current_page + 1)
        }

        const getPaginationPrev = () => {
            if (!is_first_page.value) getPaginationPage(pagination.current_page - 1)
        }

        const getParams = () => {
            let params = []

            params.push({ key: 'per_page', value: pagination.per_page })
            params.push({ key: 'page', value: pagination.current_page })
            params.push({ key: 'asset_id', value: props.asset_id })

            return params
        }
    	
        const getEligibleSales = async () => {
            if (!props.asset_id || state.sales_loading) return

            state.error_message = null

            try {
                state.sales_loading = true
                const params = getParams()
                const sales = await SaleService.getEligibleSales(params)

                state.sales = sales.sales

                pagination.current_page = sales?.pagination?.current_page || 1
                pagination.per_page = sales?.pagination?.per_page || 10
                pagination.last_page = sales?.pagination?.last_page || 1
                pagination.total = sales?.pagination?.total || 0

                

            } catch (error) {
                console.error('SaleService.getEligibleSales', error)
                state.error_message = 'No eligible sales'

            } finally {
                state.sales_loading = false
                state.sales_loaded = true
            }
        }

        const saleSelected = selected_sale => {
            emit('added', selected_sale)
        }

        const getAsset = async () => {
            try {
                const asset = await AssetService.show(props.asset_id)
                state.asset = asset
            } catch (error) {

            }
        }

        watch(
            () => props.asset_id,
            (new_value, old_value) => {
                if (new_value && new_value != old_value) {
                    getAsset()
                    getEligibleSales()
                }
            }
        )

        onMounted(() => {
            getAsset()
            getEligibleSales()
        })

        return {
            state,
            visible_sales,
            show_more_enabled,
            saleSelected,
            can_add_to_sale
        }
    }
}
</script>